import React, { useCallback, useEffect, useId, useRef } from "react";
import { useLocation as useRouterLocation } from "react-router-dom";
import { useSearch, PLANES, DYNAMIC_PACKAGES } from "../../../../contexts/SearchContext";
import { useFlightOfferUi } from "../../../../contexts/FlightOfferUiContext";
import { useBookingFlow } from "../hooks/useBookingFlow";
import SearchBlockContainer from "../../../Agent/SearchBars/Blocks/SearchBlockContainer";
import FlightOfferCompactSummary from "../../../Agent/SearchBars/Blocks/FlightOfferCompactSummary";
import "./FlightResultsTopBar.css";

/** סיכום / תשלום / סיום — בלי עורך חיפוש כדי למקד באישור ותשלום */
const HIDE_SEARCH_BAR_STEPS = new Set(["review", "draftPayment", "success"]);

/**
 * עמוד תוצאות: סרגל סיכום קומפקטי; «ערוך חיפוש» פותח פאנל עם SearchBlockContainer (קטגוריות + שדות).
 *
 * State (אין התנגשות עם SearchBlockContainer):
 * - flightSearchEditorOpen — FlightOfferUiContext בלבד; פותח/סוגר את fw-fr-editor למטה.
 * - flightSearchExpanded — מצב מקומי ב-SearchBlockContainer בזרימת חיפוש רגילה (לא embedded כאן).
 */
export default function FlightResultsTopBar() {
  const panelId = useId();
  const summaryHeaderId = useId();
  const routerLocation = useRouterLocation();
  const { currentStep } = useBookingFlow();
  const {
    loading,
    searchTab,
    location,
    calendarDateArrival,
    calendarDateLeave,
    roomInfo,
    flightTripType,
  } = useSearch();
  const {
    flightSearchEditorOpen,
    setFlightSearchEditorOpen,
    flightEditorCloseAfterLoadingCycleRef,
    clearFlightEditorPendingCloseAfterSearch,
  } = useFlightOfferUi();
  const prevLoadingRef = useRef(loading);

  const isSupportedFlightSearchTab =
    searchTab === PLANES || searchTab === DYNAMIC_PACKAGES;
  const passengersCount = (roomInfo?.rooms || []).reduce(
    (sum, room) =>
      sum +
      (Number(room?.adults) || 0) +
      (Number(room?.kids) || 0) +
      (Number(room?.infants) || 0),
    0
  );
  const hasDestination = Array.isArray(location)
    ? location.length > 0
    : Boolean(location);
  const hasDepartureDate = Boolean(calendarDateArrival);
  const needsReturnDate = flightTripType !== "OneWay";
  const hasReturnDate = Boolean(calendarDateLeave);
  const hasValidFlightSearchState =
    hasDestination &&
    hasDepartureDate &&
    (!needsReturnDate || hasReturnDate) &&
    passengersCount > 0;

  const toggleEditor = useCallback(() => {
    const next = !flightSearchEditorOpen;
    clearFlightEditorPendingCloseAfterSearch();
    if (import.meta.env.DEV) {
      console.log("[FLIGHTS_SEARCH_ACCORDION_TOGGLE]", {
        source: "FlightResultsTopBar.toggleEditor",
        flightSearchEditorOpenBefore: flightSearchEditorOpen,
        nextOpen: next,
      });
    }
    setFlightSearchEditorOpen(next);
  }, [
    flightSearchEditorOpen,
    setFlightSearchEditorOpen,
    clearFlightEditorPendingCloseAfterSearch,
  ]);

  /** סגירת העורך רק אחרי submit מתוך העורך (מסומן ב-ref), לא על כל מעבר loading גלובלי */
  useEffect(() => {
    const prev = prevLoadingRef.current;
    prevLoadingRef.current = loading;
    if (!flightSearchEditorOpen) return;
    if (!flightEditorCloseAfterLoadingCycleRef.current) return;
    if (prev && !loading) {
      flightEditorCloseAfterLoadingCycleRef.current = false;
      setFlightSearchEditorOpen(false);
    }
  }, [
    loading,
    flightSearchEditorOpen,
    setFlightSearchEditorOpen,
    flightEditorCloseAfterLoadingCycleRef,
  ]);

  useEffect(() => {
    console.log("[FLIGHT_SEARCH_DEBUG] collapsed/searchPanelOpen", {
      collapsed: !flightSearchEditorOpen,
      searchPanelOpen: flightSearchEditorOpen,
      loading,
      hasValidFlightSearchState,
    });
  }, [flightSearchEditorOpen, loading, hasValidFlightSearchState]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[SEARCH_EDIT_LAYOUT_DEBUG]", {
      route: routerLocation?.pathname || "",
      isEditMode: flightSearchEditorOpen,
      activeProductTab: searchTab,
      expandedSections: {
        flightResultsEditor: flightSearchEditorOpen,
      },
      affectedComponents: [
        "FlightResultsTopBar",
        "SearchBlockContainer(embeddedFlightResultsEditor)",
        "SearchFieldsBlock",
      ],
    });
  }, [routerLocation?.pathname, flightSearchEditorOpen, searchTab]);

  useEffect(() => {
    return () => setFlightSearchEditorOpen(false);
  }, [setFlightSearchEditorOpen]);

  useEffect(() => {
    if (HIDE_SEARCH_BAR_STEPS.has(currentStep) && flightSearchEditorOpen) {
      setFlightSearchEditorOpen(false);
    }
  }, [currentStep, flightSearchEditorOpen, setFlightSearchEditorOpen]);

  if (!isSupportedFlightSearchTab || HIDE_SEARCH_BAR_STEPS.has(currentStep)) {
    return null;
  }

  return (
    <div className="fw-fr-top" dir="rtl">
      <div className="fw-fr-top__bar">
        <div className="fw-fr-top__bar-main">
          {hasValidFlightSearchState ? (
            <FlightOfferCompactSummary
              className="flight-offer-compact-summary--results-bar"
              onEditSearch={toggleEditor}
              editorOpen={flightSearchEditorOpen}
              accordionHeaderId={summaryHeaderId}
              accordionControlsId={panelId}
              collapsedHint={
                loading
                  ? "טוען תוצאות…"
                  : "לחץ להצגת עורך חיפוש ופילטרים"
              }
            />
          ) : loading ? (
            <div className="fw-fr-top__bar-loading-sentinel" aria-hidden="true" />
          ) : (
            <div className="flight-offer-compact-summary flight-offer-compact-summary--results-bar">
              <span>נתוני החיפוש חסרים</span>
              <button
                type="button"
                className="flight-offer-compact-summary__edit"
                onClick={() => {
                  clearFlightEditorPendingCloseAfterSearch();
                  setFlightSearchEditorOpen(true);
                }}
              >
                התחל חיפוש חדש
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        id={panelId}
        role="region"
        aria-labelledby={summaryHeaderId}
        className={`fw-fr-editor ${flightSearchEditorOpen ? "fw-fr-editor--open" : ""}`}
        aria-hidden={!flightSearchEditorOpen}
      >
        <div className="fw-fr-editor__inner">
          <SearchBlockContainer embeddedFlightResultsEditor />
        </div>
      </div>
    </div>
  );
}
